import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useMutationInterop, useQueryInterop } from '@confluence/apollo-relay-interop';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam';
import { fg } from '@confluence/feature-gating';

import type {
	useEmojiEverSetPropertyQuery$data,
	useEmojiEverSetPropertyQuery$variables,
} from './__generated__/useEmojiEverSetPropertyQuery.graphql';
import type {
	useEmojiEverSetPropertyMutation$data,
	useEmojiEverSetPropertyMutation$variables,
} from './__generated__/useEmojiEverSetPropertyMutation.graphql';

const EMOJI_EVER_SET_PROPERTY_KEY = 'emoji-ever-set';

type Property =
	| {
			readonly id: string | null | undefined;
			readonly key: string | null | undefined;
			readonly value: string | null | undefined;
	  }
	| null
	| undefined;

type EmojiEverSetProperties = {
	hasEverSetEmoji: boolean;
	setEmojiEverSet: (value?: boolean) => Promise<boolean>;
	loading: boolean;
	error: any;
	updating: boolean;
};

export function useEmojiEverSetProperty({
	pageAri,
	contentId,
}: {
	pageAri: string | null;
	contentId: string;
}): EmojiEverSetProperties {
	const { data, error, loading, refetch } = useQueryInterop<
		useEmojiEverSetPropertyQuery$data,
		useEmojiEverSetPropertyQuery$variables
	>(
		/** @schema cc */
		graphql`
			query useEmojiEverSetPropertyQuery($contentId: ID, $status: [String], $draftShareId: String) {
				content(id: $contentId, draftShareId: $draftShareId, status: $status) {
					nodes {
						id
						properties(key: "emoji-ever-set") {
							nodes {
								id
								key
								value
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
				status: ['current', 'draft'],
				draftShareId: getDraftShareIdFromQueryParam(),
			},
			fetchPolicy: 'cache-and-network',
			skip: !fg('auto_page_emoji_kill_switch'),
		},
	);

	const [createPropertyMutation, { loading: updating }] = useMutationInterop<
		useEmojiEverSetPropertyMutation$data,
		useEmojiEverSetPropertyMutation$variables
	>(
		/** @schema agg */ graphql`
			mutation useEmojiEverSetPropertyMutation($input: ConfluenceCreatePagePropertyInput!) {
				confluence {
					createPageProperty(input: $input) {
						success
						pageProperty {
							key
							value
						}
						errors {
							message
						}
					}
				}
			}
		`,
		{
			context: {
				headers: {
					'X-ExperimentalApi': 'confluence-agg-beta',
				},
			},
		},
	);

	const setEmojiEverSet = useCallback(
		async (value: boolean = true) => {
			if (!contentId || !pageAri || !fg('auto_page_emoji_kill_switch')) {
				return false;
			}

			try {
				const result = await createPropertyMutation({
					variables: {
						input: {
							pageId: pageAri,
							key: EMOJI_EVER_SET_PROPERTY_KEY,
							value: String(value),
						},
					},
				});
				const success = result?.data?.confluence?.createPageProperty?.success;

				if (success) {
					void refetch();
				}
				return success || false;
			} catch (err) {
				//TODO: add mutation error handling
				return false;
			}
		},
		[contentId, pageAri, createPropertyMutation, refetch],
	);

	if (!contentId || !pageAri || !fg('auto_page_emoji_kill_switch')) {
		// By default, we don't want to set the emoji if we don't know the result so hasEverSetEmoji is true.
		return {
			hasEverSetEmoji: true,
			setEmojiEverSet: () => Promise.resolve(false),
			loading: false,
			error: null,
			updating: false,
		};
	}

	const currentValue = data?.content?.nodes?.[0]?.properties?.nodes?.find(
		(prop: Property) => prop?.key === EMOJI_EVER_SET_PROPERTY_KEY,
	)?.value;

	const hasEverSetEmoji = currentValue === 'true';

	return {
		hasEverSetEmoji,
		setEmojiEverSet,
		loading,
		error,
		updating,
	};
}
